import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, useOrg } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, CreditCard, Trash2, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";

const PaymentsPage = () => {
  const { currentOrg } = useOrg();
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    invoice_id: "",
    amount: "",
    payment_method: "cash",
    reference_number: "",
    notes: ""
  });

  const fetchData = async () => {
    if (!currentOrg) return;
    try {
      const [paymentsRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/payments`, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        }),
        axios.get(`${API}/invoices`, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id },
          params: { status: 'sent' }
        })
      ]);
      setPayments(paymentsRes.data);
      setInvoices(invoicesRes.data.filter(inv => inv.balance_due > 0));
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentOrg]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payments`, {
        invoice_id: form.invoice_id,
        amount: parseFloat(form.amount),
        payment_method: form.payment_method,
        reference_number: form.reference_number,
        notes: form.notes
      }, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      toast.success("Payment recorded");
      setDialogOpen(false);
      setForm({ invoice_id: "", amount: "", payment_method: "cash", reference_number: "", notes: "" });
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const handleDelete = async (paymentId) => {
    if (!confirm("Are you sure you want to delete this payment?")) return;
    try {
      await axios.delete(`${API}/payments/${paymentId}`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      toast.success("Payment deleted");
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete payment");
    }
  };

  const selectedInvoice = invoices.find(inv => inv.invoice_id === form.invoice_id);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-medium text-zinc-900">Payments</h1>
          <p className="text-muted-foreground text-sm">Track payments received</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="record-payment-btn">
              <Plus className="w-4 h-4" />
              Record Payment
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Invoice *</Label>
                <Select value={form.invoice_id} onValueChange={(v) => setForm({...form, invoice_id: v})}>
                  <SelectTrigger data-testid="payment-invoice-select">
                    <SelectValue placeholder="Select invoice" />
                  </SelectTrigger>
                  <SelectContent>
                    {invoices.map(inv => (
                      <SelectItem key={inv.invoice_id} value={inv.invoice_id}>
                        {inv.invoice_number} - {inv.customer?.business_name} (${inv.balance_due?.toFixed(2)} due)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({...form, amount: e.target.value})}
                  placeholder={selectedInvoice ? `Max: $${selectedInvoice.balance_due?.toFixed(2)}` : "Enter amount"}
                  required
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({...form, payment_method: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Credit Card</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference Number</Label>
                <Input
                  value={form.reference_number}
                  onChange={(e) => setForm({...form, reference_number: e.target.value})}
                  placeholder="Transaction ID, check number, etc."
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-payment-btn">Record Payment</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Payments Table */}
      <div className="bg-white border border-zinc-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : payments.length === 0 ? (
          <div className="p-16 text-center">
            <CreditCard className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-muted-foreground">No payments recorded yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Invoice</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Method</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Reference</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Amount</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.payment_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-3 px-4 text-muted-foreground">{payment.payment_date}</td>
                    <td className="py-3 px-4">
                      <Link 
                        to={`/dashboard/invoices/${payment.invoice_id}`}
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        <LinkIcon className="w-3 h-3" />
                        View Invoice
                      </Link>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-zinc-100 text-zinc-700 capitalize">
                        {payment.payment_method?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{payment.reference_number || '-'}</td>
                    <td className="py-3 px-4 text-right font-medium text-emerald-600">
                      +${payment.amount?.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(payment.payment_id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsPage;
